import { useLocation } from "wouter";
import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft, Edit } from "lucide-react";
import { formatCurrency } from "@/lib/currency";
import { useAuth } from "@/lib/auth-context";

export default function ViewStarterEquipment() {
  const [location, setLocation] = useLocation();
  const [equipmentId, setEquipmentId] = useState<string | null>(null);
  const { user } = useAuth();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get('id');
    setEquipmentId(id);
  }, [location]);

  const { data: equipmentData, isLoading: equipmentLoading } = useQuery({
    queryKey: ["/api/starter-pack/equipment"],
  });

  const { data: categoriesData } = useQuery({
    queryKey: ["/api/starter-pack/categories/equipment"],
  });

  const equipment = Array.isArray(equipmentData) ? equipmentData : [];
  const categories = Array.isArray(categoriesData) ? categoriesData : [];

  const equipmentItem = equipmentId && Array.isArray(equipment)
    ? equipment.find((e: any) => e._id === equipmentId || e.id === equipmentId)
    : undefined;

  const category = equipmentItem?.categoryId && Array.isArray(categories)
    ? categories.find((c: any) => c._id === equipmentItem.categoryId)?.name || 'Uncategorized'
    : 'Uncategorized';

  if (equipmentLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-12 w-64" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (!equipmentItem) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => setLocation("/starter-pack")}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-xl sm:text-3xl font-bold">Equipment not found</h1>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => setLocation("/starter-pack")}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-xl sm:text-3xl font-bold tracking-tight">{equipmentItem.name}</h1>
            <p className="text-muted-foreground">Starter Pack Equipment</p>
          </div>
        </div>
        <Button onClick={() => setLocation(`/starter-pack/equipment?edit=${equipmentItem._id || equipmentItem.id}`)}>
          <Edit className="h-4 w-4 mr-2" />
          Edit
        </Button>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground">Name</label>
              <p className="text-lg">{equipmentItem.name}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Category</label>
              <p className="text-lg">{category}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Purchase Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground">Purchase Amount</label>
              <p className="text-lg">{formatCurrency(Number(equipmentItem.purchaseAmount || 0), user?.currency || "USD")}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {(equipmentItem.image || equipmentItem.imageUrl) && (
        <Card>
          <CardHeader>
            <CardTitle>Image</CardTitle>
          </CardHeader>
          <CardContent>
            <img
              src={equipmentItem.image || equipmentItem.imageUrl}
              alt={equipmentItem.name}
              className="w-full max-w-md mx-auto rounded-lg object-cover"
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
}
